import { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from './useAppDispatch';
import { selectConfigValue } from '../selectors/config';
import { getYandexData } from '../services/ymaps';
import { addNotification } from '../slices';
import { ENotificationType } from '../types/notifications';
import { ICoordinates } from '../types/offers';
import { loadYmapsApi } from '../utils/ymaps';

const pin = require('./assets/Pin.svg');
const pinFavorite = require('./assets/PinFavorite.svg');

type TUseProps = {
  isOpen: boolean;
  isFavorite: boolean;
  coordinates: ICoordinates | null;
};

export const useYmaps = ({ isOpen, coordinates, isFavorite }: TUseProps) => {
  const container = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  const apiKey = useSelector(selectConfigValue('apiKey'));

  const successCallback = useCallback(
    (ymaps: YMaps.IYMaps) => {
      if (container.current && coordinates) {
        const { lat = 0, lng = 0 } = coordinates;

        // Смещение центра карты для того, чтобы иконка оказалась по центру
        const OFFSET_BY_ICON_SIZE = 0.001;

        const map = new ymaps.Map(container.current, {
          behaviors: ['drag', 'dblClickZoom', 'rightMouseButtonMagnifier'],
          center: [lat + OFFSET_BY_ICON_SIZE, lng],
          zoom: 14,
        });

        map.controls.add('zoomControl', {
          position: {
            left: 10,
            top: 92,
          },
        });

        const myPlacemark = new ymaps.Placemark(
          [lat, lng],
          {},
          {
            iconImageOffset: [-27, -54],
            iconImageSize: [54, 54],
            iconLayout: 'default#image',
            iconImageHref: isFavorite ? pinFavorite : pin,
          },
        );

        map.geoObjects.add(myPlacemark);
      }
    },
    [coordinates, isFavorite],
  );

  const errorCallback = useCallback(() => {
    dispatch(addNotification({ type: ENotificationType.Error, text: 'Не удалось загрузить карту' }));
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      getYandexData({
        request: loadYmapsApi({ apiKey }),
        onSuccess: successCallback,
        onError: errorCallback,
      });
    }
  }, [apiKey, errorCallback, isOpen, successCallback]);

  return container;
};
